import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Store, 
  DollarSign, 
  Package, 
  Settings, 
  ArrowRight, 
  TrendingUp, 
  ShoppingCart, 
  Wallet, 
  Plus, 
  ExternalLink, 
  FileText,
  Clock,
  CheckCircle2,
  ChevronRight,
  Calculator,
  Layers,
  Box,
  XCircle
} from "lucide-react";
import { getStorePreviewUrl } from "../../utils/storeUtils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
});

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  processing: { label: "Processing", color: "bg-purple-100 text-purple-800", icon: Package },
  shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800", icon: Box },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle }
};

export default function ResellerOverview({ store, onUpdate }) {
  const [earnings, setEarnings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [earningsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/earnings/my`, getAuthHeaders()),
        axios.get(`${API}/reseller/orders`, getAuthHeaders()),
      ]);
      setEarnings(earningsRes.data);
      const ordersData = ordersRes.data;
      if (Array.isArray(ordersData)) {
        setOrders(ordersData);
      } else if (ordersData && typeof ordersData === 'object' && Array.isArray(ordersData.orders)) {
        setOrders(ordersData.orders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = [
    {
      label: "Net Earnings",
      value: `$${(earnings?.earnings?.total_earnings || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subValue: "Total profit from markups",
      icon: TrendingUp,
      color: "emerald",
      link: "/reseller/earnings"
    },
    {
      label: "Total Sales",
      value: (orders?.length || 0).toString(),
      subValue: `${orders?.filter(o => o.status === 'delivered').length || 0} completed orders`,
      icon: ShoppingCart,
      color: "indigo",
      link: "/reseller/orders"
    },
    {
      label: "Available Balance",
      value: `$${(earnings?.earnings?.available_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subValue: "Ready to withdraw",
      icon: Wallet,
      color: "amber",
      link: "/reseller/earnings"
    },
  ];

  const quickLinks = [
    { label: "Pricing Settings", icon: DollarSign, link: "/reseller/pricing", color: "bg-blue-50 text-blue-600" },
    { label: "My Products", icon: Package, link: "/reseller/products", color: "bg-purple-50 text-purple-600" },
    { label: "My Collections", icon: Layers, link: "/reseller/collections", color: "bg-amber-50 text-amber-600" },
    { label: "Business Info", icon: Settings, link: "/reseller/settings", color: "bg-slate-50 text-slate-600" },
  ];

  const chartData = earnings?.chart_data || [];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500" data-testid="reseller-overview">
      {/* Header & Quick Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Welcome back, {store.business_name}
          </h1>
          <p className="text-slate-500 font-medium">Here's what's happening with your store today.</p>
        </div>
        <div className="flex items-center gap-3">
          <a 
            href={getStorePreviewUrl(store.subdomain)} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="rounded-xl border-slate-200 hover:bg-slate-50 gap-2">
              <ExternalLink className="w-4 h-4" />
              View Shop
            </Button>
          </a>
          <Link to="/reseller/pricing">
            <Button className="rounded-xl bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200/50 gap-2">
              <Calculator className="w-4 h-4" />
              Manage Pricing
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <Link 
            key={index} 
            to={stat.link}
            className="group bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-2xl ${
                stat.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
                stat.color === 'indigo' ? 'bg-indigo-50 text-indigo-600' :
                'bg-amber-50 text-amber-600'
              } transition-transform group-hover:scale-110`}>
                <stat.icon size={24} />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-600 transition-colors" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">{stat.label}</p>
              <h3 className="text-3xl font-black text-slate-900 mb-1">{stat.value}</h3>
              <p className="text-xs text-slate-500 font-medium">{stat.subValue}</p>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Performance Graph */}
        <div className="lg:col-span-2 bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-xl font-bold text-slate-900">Earning Performance</h3>
              <p className="text-sm text-slate-500 font-medium">Last 30 days revenue trend</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-indigo-500" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Commission</span>
            </div>
          </div>
          
          <div className="h-[300px] w-full">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorEarningsPanel" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(val) => val.split('-').slice(1).join('/')}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                    tickFormatter={(val) => `$${val}`}
                  />
                  <Tooltip
                    contentStyle={{ border: 'none', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    itemStyle={{ fontSize: '12px', fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="earnings"
                    stroke="#6366f1"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#colorEarningsPanel)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <TrendingUp size={40} className="mb-4 opacity-20" />
                <p className="text-sm font-medium">No performance data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Links & Info */}
        <div className="space-y-8">
          {/* Quick Links */}
          <div className="bg-slate-900 rounded-[2rem] p-8 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="text-lg font-bold mb-6">Quick Links</h3>
              <div className="grid grid-cols-2 gap-3">
                {quickLinks.map((link, idx) => (
                  <Link 
                    key={idx} 
                    to={link.link}
                    className="flex flex-col items-center justify-center p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${link.color}`}>
                      <link.icon size={20} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest text-center">{link.label}</span>
                  </Link>
                ))}
              </div>
            </div>
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full translate-x-12 -translate-y-12"></div>
          </div>

          {/* Store Info */}
          <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm relative overflow-hidden group">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Store Overview</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 italic transition-transform group-hover:scale-[1.02]">
                <span className="text-xs font-bold text-slate-500">Multiplier</span>
                <span className="text-sm font-black text-indigo-600">{store.global_price_multiplier}x</span>
              </div>
            </div>
            <Link to="/reseller/settings" className="mt-6 flex items-center justify-center gap-2 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors">
              Store Settings <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Orders Section */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Recent Orders</h3>
            <p className="text-sm text-slate-500 font-medium">Quick overview of your latest sales</p>
          </div>
          <Link to="/reseller/orders">
            <Button variant="ghost" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 font-bold text-xs uppercase tracking-widest">
              View All <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          {orders.length > 0 ? (
            <table className="w-full text-left">
              <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                <tr>
                  <th className="px-8 py-4">Order ID</th>
                  <th className="px-8 py-4">Date</th>
                  <th className="px-8 py-4">Customer</th>
                  <th className="px-8 py-4">Status</th>
                  <th className="px-8 py-4 text-right">Earning</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {orders.slice(0, 5).map((order) => (
                  <tr key={order.order_id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-5 font-mono font-bold text-slate-900 text-sm">
                      #{order.order_id.slice(-8).toUpperCase()}
                    </td>
                    <td className="px-8 py-5 text-sm text-slate-500 font-medium">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-8 py-5">
                      <p className="text-sm font-bold text-slate-900">{order.customer_name || 'Guest'}</p>
                      <p className="text-xs text-slate-400">{order.customer_email}</p>
                    </td>
                    <td className="px-8 py-5">
                      <Badge className={`rounded-xl shadow-none px-3 py-1 font-bold text-[10px] uppercase tracking-wider ${
                        order.status === 'delivered' ? 'bg-emerald-50 text-emerald-700 border-none' :
                        order.status === 'pending' ? 'bg-amber-50 text-amber-700 border-none' :
                        'bg-blue-50 text-blue-700 border-none'
                      }`}>
                        {order.status}
                      </Badge>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <span className="text-emerald-600 font-black text-sm">
                        +${(order.reseller_commission || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-slate-400">
              <ShoppingCart size={40} className="mx-auto mb-4 opacity-20" />
              <p className="text-sm font-medium">No orders found yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
